/**
 * Ticket SLA dashboard.
 *
 * Layout:
 *   PageHeader → HeroPanel (within / at-risk / breached %)
 *              → Burn-rate chart (30-day creation density)
 *              → Breach reel (cards of currently-breached tickets)
 *              → Per-priority breakdown table (p50/p90 from snapshot)
 *
 * The /internal/sla/tickets snapshot exposes aggregate p50/p90 numbers
 * and current counters (open / awaiting_staff / stale_awaiting_customer).
 * For per-ticket breach state we replay the priority-based SLA targets
 * (urgent ≤1h, high ≤4h, normal ≤24h, low ≤72h) against last_activity_at.
 */
import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import {
  Gauge, RefreshCw, Clock, ShieldCheck,
  TrendingUp, Flame, Users, MessageCircle,
} from 'lucide-react';
import {
  AdminApiClient,
  type TicketSlaSummary, type CustomerTicketRow, type TicketPriority,
} from '../api';
import { useSession } from '../session';
import { PageHeader } from '../components/ui/PageHeader';
import { HeroPanel, HeroKpi } from '../components/ui/HeroPanel';
import { Button } from '../components/ui/Button';
import { StatusChip, type Band } from '../components/ui/StatusChip';
import { FetchState } from '../components/ui/FetchState';
import { failureFrom, type Failure } from '../lib/api-state';

const SLA_TARGET_HOURS: Record<TicketPriority, number> = {
  urgent: 1, high: 4, normal: 24, low: 72,
};
const PRIORITY_TONES: Record<TicketPriority, string> = {
  urgent: '#ef4444',
  high:   '#fb923c',
  normal: '#22d3ee',
  low:    '#94a3b8',
};

interface BreachInfo {
  state: 'within' | 'at_risk' | 'breached';
  remainingH: number; // negative when breached
}
function evaluateSla(t: CustomerTicketRow): BreachInfo {
  const target = SLA_TARGET_HOURS[t.priority];
  const ageH = (Date.now() - new Date(t.last_activity_at).getTime()) / 1000 / 3600;
  const remaining = target - ageH;
  if (remaining < 0) return { state: 'breached', remainingH: remaining };
  if (remaining < target * 0.25) return { state: 'at_risk', remainingH: remaining };
  return { state: 'within', remainingH: remaining };
}

export function SlaDashboardPage(): JSX.Element {
  const { state } = useSession();
  const api = useMemo(
    () => new AdminApiClient({ baseUrl: '', getToken: () => state.token }),
    [state.token],
  );

  const [summary, setSummary] = useState<TicketSlaSummary | null>(null);
  const [tickets, setTickets] = useState<CustomerTicketRow[] | null>(null);
  const [allTickets, setAllTickets] = useState<CustomerTicketRow[] | null>(null);
  const [err, setErr] = useState<Failure | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [s, oa, all] = await Promise.all([
        api.getTicketSla(),
        api.customerTickets('open_or_awaiting').then((r) => r.tickets),
        api.customerTickets('open' /* server treats unknown as default — gives full set */)
          .then((r) => r.tickets)
          .catch((e) => { failureFrom(e, 'ticket history'); return null; }),
      ]);
      setSummary(s);
      setTickets(oa);
      setAllTickets(all);
      setErr(null);
      setUpdatedAt(new Date());
    } catch (e) {
      setErr(failureFrom(e, 'the SLA data'));
    } finally {
      setRefreshing(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
    const id = setInterval(() => void load(), 60_000);
    return () => clearInterval(id);
  }, [load]);

  const liveSlaState = useMemo(() => {
    let within = 0, atRisk = 0, breached = 0;
    const breachedRows: Array<{ t: CustomerTicketRow; info: BreachInfo }> = [];
    for (const t of tickets ?? []) {
      if (t.status !== 'open' && t.status !== 'awaiting_staff') continue;
      const info = evaluateSla(t);
      if (info.state === 'within') within += 1;
      else if (info.state === 'at_risk') atRisk += 1;
      else {
        breached += 1;
        breachedRows.push({ t, info });
      }
    }
    breachedRows.sort((a, b) => a.info.remainingH - b.info.remainingH);
    const total = within + atRisk + breached;
    return {
      within, atRisk, breached,
      total,
      withinPct: total > 0 ? Math.round((within / total) * 100) : 0,
      atRiskPct: total > 0 ? Math.round((atRisk / total) * 100) : 0,
      breachedPct: total > 0 ? Math.round((breached / total) * 100) : 0,
      breachedRows,
    };
  }, [tickets]);

  const perPriority = useMemo(() => {
    const out: Array<{
      priority: TicketPriority;
      live: number;
      breached: number;
    }> = (['urgent', 'high', 'normal', 'low'] as TicketPriority[]).map((p) => ({
      priority: p, live: 0, breached: 0,
    }));
    for (const t of tickets ?? []) {
      if (t.status !== 'open' && t.status !== 'awaiting_staff') continue;
      const slot = out.find((x) => x.priority === t.priority);
      if (!slot) continue;
      slot.live += 1;
      if (evaluateSla(t).state === 'breached') slot.breached += 1;
    }
    return out;
  }, [tickets]);

  return (
    <div className="page">
      <PageHeader
        title="Ticket SLA"
        subtitle={
          updatedAt
            ? `Auto-refreshes every 60s · last update ${updatedAt.toLocaleTimeString()}`
            : 'Auto-refreshes every 60s'
        }
        icon={Gauge}
        accent="#f97316"
        actions={
          <Button variant="outline" size="sm" onClick={() => void load()}>
            <RefreshCw width={14} height={14}
              style={{ animation: refreshing ? 'spin 900ms linear infinite' : undefined }} />
            Refresh
          </Button>
        }
      />

      {/* No data at all: loading or failed - never render a zero/healthy pulse. */}
      {(!summary || !tickets) && (
        <FetchState loading={!err} error={err} onRetry={() => void load()} skeleton="cards" />
      )}
      {summary && tickets && err && (
        <p className="err" role="alert" style={{ fontSize: '0.8125rem', marginBottom: 12 }}>
          Could not refresh: {err.message} Showing the last data loaded{updatedAt ? ` at ${updatedAt.toLocaleTimeString()}` : ''}.
        </p>
      )}

      {summary && tickets && (
      <>
      <HeroPanel
        icon={Gauge}
        accent="#f97316"
        title="SLA pulse"
        subtitle="Live breach state across the open + awaiting-staff queue."
        kpis={liveSlaState.total === 0 ? [
          <HeroKpi key="w" label="Within SLA" value="—" band="neutral" hint="no live tickets" />,
          <HeroKpi key="r" label="At risk" value="—" band="neutral" hint="no live tickets" />,
          <HeroKpi key="b" label="Breached" value="—" band="neutral" hint="no live tickets" />,
          <HeroKpi key="o" label="Open queue" value={summary.open_count} />,
          <HeroKpi key="a" label="Awaiting staff" value={summary.awaiting_staff_count}
                   band={summary.awaiting_staff_count > 5 ? 'warning' : 'neutral'} />,
        ] : [
          <HeroKpi key="w" label="Within SLA" value={`${liveSlaState.withinPct}%`}
                   band={liveSlaState.withinPct >= 90 ? 'healthy'
                        : liveSlaState.withinPct >= 70 ? 'degraded'
                        : 'critical'}
                   hint={`${liveSlaState.within} tickets`} />,
          <HeroKpi key="r" label="At risk" value={`${liveSlaState.atRiskPct}%`}
                   band={liveSlaState.atRiskPct === 0 ? 'healthy' : 'warning'}
                   hint={`${liveSlaState.atRisk} <25% remaining`} />,
          <HeroKpi key="b" label="Breached" value={`${liveSlaState.breachedPct}%`}
                   band={liveSlaState.breached === 0 ? 'healthy' : 'critical'}
                   hint={`${liveSlaState.breached} over target`} />,
          <HeroKpi key="o" label="Open queue" value={summary.open_count} />,
          <HeroKpi key="a" label="Awaiting staff" value={summary.awaiting_staff_count}
                   band={summary.awaiting_staff_count > 5 ? 'warning' : 'neutral'} />,
        ]}
      />

      <div style={{ marginTop: 16 }}>
        {allTickets
          ? <BurnRateChart tickets={allTickets} />
          : <p role="alert" style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', margin: 0 }}>
              Inbound-load history is unavailable right now.
            </p>}
      </div>

      <div style={{ marginTop: 16 }}>
        <BreachReel rows={liveSlaState.breachedRows} />
      </div>

      <div style={{ marginTop: 16 }}>
        <PerPriorityBreakdown
          summary={summary}
          perPriority={perPriority}
        />
      </div>
      </>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Burn-rate chart — daily ticket creation density over 30 days.
 * Without a historical SLA endpoint, ticket-volume is the closest
 * leading indicator we can show: high-volume days = high pressure.
 * ────────────────────────────────────────────────────────────────── */

function BurnRateChart({ tickets }: { tickets: CustomerTicketRow[] }): JSX.Element {
  const { days, max, total } = useMemo(() => {
    const now = new Date();
    const out: Array<{ key: string; label: string; count: number }> = [];
    for (let i = 29; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
      out.push({
        key: d.toISOString().slice(0, 10),
        label: d.toLocaleDateString('en-US', { month: 'short', day: '2-digit' }),
        count: 0,
      });
    }
    const idx = new Map(out.map((d, i) => [d.key, i] as const));
    let total = 0;
    for (const t of tickets) {
      const k = t.created_at.slice(0, 10);
      const i = idx.get(k);
      if (i !== undefined && out[i]) {
        out[i].count += 1;
        total += 1;
      }
    }
    const max = Math.max(1, ...out.map((d) => d.count));
    return { days: out, max, total };
  }, [tickets]);

  return (
    <section
      style={{
        padding: 16,
        borderRadius: 14,
        border: '1px solid var(--border)',
        background: 'var(--bg-raised)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <TrendingUp width={14} height={14} color="var(--text-muted)" />
        <span
          style={{
            fontSize: '0.6875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--text-muted)',
            fontWeight: 600,
          }}
        >
          Inbound load · last 30 days
        </span>
        <span
          style={{
            marginLeft: 'auto',
            fontSize: '0.6875rem',
            color: 'var(--text-muted)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {total} tickets
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))`,
          gap: 3,
          alignItems: 'end',
          height: 90,
        }}
      >
        {days.map((d) => {
          const h = (d.count / max) * 100;
          const tone = d.count >= max * 0.75 ? '#f97316'
                     : d.count >= max * 0.4 ? '#fbbf24'
                     : '#22d3ee';
          return (
            <div
              key={d.key}
              title={`${d.label} · ${d.count} ticket${d.count === 1 ? '' : 's'}`}
              style={{
                height: `${Math.max(2, h)}%`,
                background: d.count > 0
                  ? `linear-gradient(180deg, ${tone}, ${tone}55)`
                  : 'rgba(148,163,184,0.12)',
                borderRadius: 3,
                borderTop: d.count > 0 ? `1px solid ${tone}` : '1px solid transparent',
              }}
            />
          );
        })}
      </div>
      <p style={{ margin: '6px 0 0', fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
        Bars are ticket-creation count per day. Without a historical SLA
        endpoint, volume is the best proxy for past pressure — pair with
        the snapshot below.
      </p>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Breach reel — currently breached tickets, sorted by overage
 * ────────────────────────────────────────────────────────────────── */

function BreachReel({
  rows,
}: { rows: Array<{ t: CustomerTicketRow; info: BreachInfo }> }): JSX.Element {
  return (
    <section>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 8,
        }}
      >
        <Flame width={14} height={14} color="var(--danger)" />
        <h3
          style={{
            margin: 0,
            fontSize: '0.6875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--text-muted)',
            fontWeight: 600,
          }}
        >
          Breach reel · {rows.length}
        </h3>
      </div>

      <FetchState
        loading={false}
        empty={rows.length === 0}
        emptyTitle="Nothing on fire"
        emptyDescription="No tickets have crossed their SLA target right now. Nice work."
        emptyIcon={ShieldCheck}
      >
        <div
          style={{
            display: 'grid',
            gap: 12,
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          }}
        >
          {rows.slice(0, 12).map(({ t, info }) => (
            <BreachCard key={t.id} t={t} info={info} />
          ))}
        </div>
      </FetchState>
    </section>
  );
}

function BreachCard({
  t, info,
}: { t: CustomerTicketRow; info: BreachInfo }): JSX.Element {
  const tone = PRIORITY_TONES[t.priority];
  const overH = Math.abs(info.remainingH);
  return (
    <article
      style={{
        position: 'relative',
        padding: 14,
        borderRadius: 12,
        border: `1px solid rgba(239, 68, 68, 0.35)`,
        background: 'var(--bg-raised)',
        display: 'flex', flexDirection: 'column', gap: 8,
        overflow: 'hidden',
      }}
    >
      <span
        aria-hidden
        style={{
          position: 'absolute',
          top: 0, left: 0, bottom: 0, width: 4,
          background: tone,
        }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <StatusChip band="critical" pulse>
          {fmtH(overH)} over
        </StatusChip>
        <StatusChip band={priorityBand(t.priority)} size="sm" noDot>
          {t.priority}
        </StatusChip>
        <span style={{ marginLeft: 'auto', fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
          target {SLA_TARGET_HOURS[t.priority]}h
        </span>
      </div>
      <div
        style={{
          fontSize: '0.875rem',
          fontWeight: 500,
          color: 'var(--text-primary)',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {t.subject}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
        <Clock width={11} height={11} />
        last activity {fmtRelative(t.last_activity_at)}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
        <Users width={11} height={11} />
        customer <code>{t.customer_id.slice(0, 8)}…</code>
      </div>
    </article>
  );
}

function priorityBand(p: TicketPriority): Band {
  switch (p) {
    case 'urgent': return 'critical';
    case 'high':   return 'warning';
    case 'normal': return 'neutral';
    case 'low':    return 'neutral';
  }
}

/* ──────────────────────────────────────────────────────────────────
 * Per-priority breakdown
 * ────────────────────────────────────────────────────────────────── */

interface PercentileTarget {
  label: string;
  value: number | null;
  target_seconds: number;
}

function targetBand(value: number | null, target: number): Band {
  if (value == null) return 'neutral';
  if (value <= target) return 'healthy';
  if (value <= target * 1.5) return 'warning';
  return 'critical';
}

function PerPriorityBreakdown({
  summary, perPriority,
}: {
  summary: TicketSlaSummary | null;
  perPriority: Array<{ priority: TicketPriority; live: number; breached: number }>;
}): JSX.Element {
  const percentiles: PercentileTarget[] = summary ? [
    { label: 'First response · p50', value: summary.first_response_p50_seconds, target_seconds: 4 * 3600 },
    { label: 'First response · p90', value: summary.first_response_p90_seconds, target_seconds: 24 * 3600 },
    { label: 'Resolution · p50',     value: summary.resolution_p50_seconds,     target_seconds: 24 * 3600 },
    { label: 'Resolution · p90',     value: summary.resolution_p90_seconds,     target_seconds: 5 * 86_400 },
  ] : [];

  return (
    <section>
      <div
        style={{
          display: 'grid',
          gap: 12,
          gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)',
        }}
      >
        <div
          style={{
            padding: 16,
            borderRadius: 14,
            border: '1px solid var(--border)',
            background: 'var(--bg-raised)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Gauge width={14} height={14} color="var(--text-muted)" />
            <h3
              style={{
                margin: 0,
                fontSize: '0.6875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'var(--text-muted)',
                fontWeight: 600,
              }}
            >
              Response &amp; resolution percentiles
            </h3>
            <span style={{
              marginLeft: 'auto', fontSize: '0.6875rem', color: 'var(--text-muted)',
            }}>
              sample {summary?.sample_size ?? 0}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {percentiles.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', margin: 0 }}>
                Loading…
              </p>
            ) : (
              percentiles.map((p) => <PercentileBar key={p.label} p={p} />)
            )}
          </div>
          <p style={{ margin: '10px 0 0', fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
            Bars fill proportional to (value / 2× target). Healthy = within SLO,
            warning = within 1.5× target, critical = breach.
          </p>
        </div>

        <div
          style={{
            padding: 16,
            borderRadius: 14,
            border: '1px solid var(--border)',
            background: 'var(--bg-raised)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <MessageCircle width={14} height={14} color="var(--text-muted)" />
            <h3
              style={{
                margin: 0,
                fontSize: '0.6875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'var(--text-muted)',
                fontWeight: 600,
              }}
            >
              By priority
            </h3>
          </div>
          <ul
            style={{
              listStyle: 'none', padding: 0, margin: 0,
              display: 'flex', flexDirection: 'column', gap: 8,
            }}
          >
            {perPriority.map((p) => (
              <li
                key={p.priority}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 12px',
                  borderRadius: 10,
                  background: 'var(--bg-overlay)',
                  border: '1px solid var(--border)',
                }}
              >
                <span
                  style={{
                    width: 8, height: 8, borderRadius: 2,
                    background: PRIORITY_TONES[p.priority],
                  }}
                />
                <span style={{ fontSize: '0.8125rem', flex: 1, color: 'var(--text-primary)', textTransform: 'capitalize' }}>
                  {p.priority}
                  <span style={{ marginLeft: 6, fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
                    target {SLA_TARGET_HOURS[p.priority]}h
                  </span>
                </span>
                <span style={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums', color: 'var(--text-muted)' }}>
                  {p.live} live
                </span>
                {p.breached > 0 ? (
                  <StatusChip band="critical" size="sm">
                    {p.breached} breached
                  </StatusChip>
                ) : (
                  <StatusChip band="healthy" size="sm" noDot>
                    clean
                  </StatusChip>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function PercentileBar({ p }: { p: PercentileTarget }): JSX.Element {
  const band = targetBand(p.value, p.target_seconds);
  const tone = band === 'healthy' ? '#10b981'
             : band === 'warning' ? '#f59e0b'
             : band === 'critical' ? '#ef4444'
                                   : '#64748b';
  const pct = Math.min(100, p.value ? (p.value / (p.target_seconds * 2)) * 100 : 0);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: 4 }}>
        <span style={{ fontWeight: 500 }}>{p.label}</span>
        <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
          {fmtDuration(p.value)} · target {fmtDuration(p.target_seconds)}
        </span>
      </div>
      <div
        style={{
          height: 6,
          borderRadius: 999,
          background: 'var(--bg-overlay)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            background: `linear-gradient(90deg, ${tone}, ${tone}aa)`,
            transition: 'width 240ms ease',
          }}
        />
      </div>
    </div>
  );
}

/* Helpers */

function fmtDuration(seconds: number | null): string {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds.toFixed(0)}s`;
  if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
  if (seconds < 86_400) return `${(seconds / 3600).toFixed(1)}h`;
  return `${(seconds / 86_400).toFixed(1)}d`;
}

function fmtH(hours: number): string {
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))}m`;
  if (hours < 48) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}

function fmtRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

